import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'

/**
 * Tipe untuk parameter jenis alert.
 */
type AlertType = 'info' | 'success' | 'error' | 'warning'

/**
 * Antarmuka untuk objek alert individual.
 */
export interface Alert {
  id: number
  message: string
  bgColor: string
  type: AlertType
}

export const useAlertStore = defineStore('alerts', () => {
  // State
  const alerts: Ref<Alert[]> = ref([])
  const error: Ref<string | null> = ref(null)
  const alertIdCounter: Ref<number> = ref(0)

  // Actions
  /**
   * Menampilkan notifikasi sementara (toast).
   * @param message - Pesan yang akan ditampilkan.
   * @param type - Jenis alert ('info', 'success', 'error').
   */
  function showAlert(message: string, type: AlertType = 'info'): void {
    const id = alertIdCounter.value++
    let bgColor = 'bg-blue-500'
    if (type === 'success') bgColor = 'bg-green-500'
    if (type === 'error') bgColor = 'bg-red-500'

    alerts.value.push({ id, message, bgColor, type })

    setTimeout(() => {
      alerts.value = alerts.value.filter((a) => a.id !== id)
    }, 3000)
  }

  /**
   * Remove a specific alert immediately
   */
  function removeAlert(id: number): void {
    alerts.value = alerts.value.filter((a) => a.id !== id)
  }

  /**
   * Menampilkan pesan error persisten di UI.
   * @param message - Pesan error yang akan ditampilkan.
   */
  function showError(message: string): void {
    console.error('Store Error:', message)
    error.value = message
    // Error tidak hilang otomatis, harus dihapus manual dengan clearError
  }

  /**
   * Menghapus pesan error yang sedang ditampilkan.
   */
  function clearError(): void {
    error.value = null
  }

  return { alerts, error, showAlert, showError, clearError, removeAlert }
})
